/* Память «Студии» между показами раздела — как состояние модуля эталона: BotStudio.S живёт через смену разделов, mount рисует
   текущий вид из S сразу, unmount S не трогает (bot-studio.js: mount, unmount; APP-SPEC-3 §2.3 «Память»: вид, поиск, фильтры
   и кадр слайдера переживают смену вкладок). React-раздел снимает свой корень при уходе (definePageModule), поэтому вид, фильтры,
   поиск, стек «Назад», прокрутка и последние ответы сервера живут здесь, на уровне модуля. Возврат рисует раздел из памяти сразу,
   ответы сервера подтверждают её в фоне.
   Память принадлежит владельцу сессии (BotServerMe.user.id): у другого владельца она пуста и стирается, без известного владельца
   ничего не хранится и не выдаётся. Ответы сервера — на языке профиля (Accept-Language: названия, описания, вкладки): смена языка
   стирает документы и выдачи, а вид, фильтры, стек «Назад» и прокрутка остаются. Живёт до перезагрузки страницы. */
import type { BrowseState } from './studio-browse-state';

/** Вид просмотра на момент ухода: состояние, стек «Назад» и прокрутка раздела (у открытого экрана — место просмотра под ним) */
export interface StudioViewMemory {
  browse: BrowseState;
  stack: Array<{ state: BrowseState; scroll: number }>;
  scroll: number;
}
/** Сколько выдач (областей страниц: каталог, подборки, поиск главной) помнится */
const LISTS = 8;
const store = {
  owner: '',
  view: null as StudioViewMemory | null,
  documents: new Map<string, unknown>(),
  lists: new Map<string, unknown>(),
};

/** Владелец сессии сервера; пусто — неизвестен */
export function studioOwner(): string {
  const id = (window as unknown as { BotServerMe?: { user?: { id?: number } } }).BotServerMe?.user?.id;
  return Number.isSafeInteger(id) && Number(id) > 0 ? String(id) : '';
}

/** Память текущего владельца: сменился — прежняя стирается целиком. false — владельца нет, память не работает */
function own(): boolean {
  const owner = studioOwner();
  if (owner !== store.owner) {
    store.owner = owner;
    store.view = null;
    store.documents.clear();
    store.lists.clear();
  }
  return !!owner;
}

/* смена аккаунта (выход, слияние) стирает память сразу, не дожидаясь следующего показа «Студии» */
if (typeof document !== 'undefined') document.addEventListener('steos:account-updated', () => void own());
/* смена языка профиля: ответы прежнего языка на возврате не показываются (и не остаются, если подтверждение не пришло) */
if (typeof document !== 'undefined')
  document.addEventListener('steos:locale', () => {
    store.documents.clear();
    store.lists.clear();
  });

export function recallStudioView(): StudioViewMemory | null {
  return own() ? store.view : null;
}

export function rememberStudioView(view: StudioViewMemory) {
  if (own()) store.view = view;
}

/** Последний ответ документа (главная, каталог, «Все инструменты», число шаблонов): undefined — нет */
export function recallStudioDocument<T>(identity: string): T | undefined {
  return own() ? (store.documents.get(identity) as T | undefined) : undefined;
}

export function rememberStudioDocument(identity: string, data: unknown) {
  if (own()) store.documents.set(identity, data);
}

/** Последние выдачи по областям показа (сессиям списка), от старых к новым */
export function recallStudioLists<T>(): Map<string, T> {
  return own() ? new Map(store.lists as Map<string, T>) : new Map();
}

export function rememberStudioList(session: string, list: unknown) {
  if (!own()) return;
  store.lists.delete(session);
  store.lists.set(session, list);
  if (store.lists.size > LISTS) store.lists.delete(store.lists.keys().next().value!);
}

/** Только для тестов: забыть всё */
export function resetStudioStore() {
  store.owner = '';
  store.view = null;
  store.documents.clear();
  store.lists.clear();
}
